import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** 放在文字右側、包在自己圓形容器裡的圖示。 */
  trailingIcon?: ReactNode;
  leadingIcon?: ReactNode;
}

const BASE =
  'group relative inline-flex items-center justify-center gap-2 rounded-full font-medium ' +
  'transition-[transform,background-color,color,box-shadow] duration-300 ease-[var(--ease-gentle)] ' +
  'active:scale-[0.98] disabled:pointer-events-none disabled:opacity-45';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-white shadow-[var(--shadow-ambient)] hover:bg-accent-strong hover:shadow-[var(--shadow-raised)]',
  secondary:
    'bg-surface-raised text-text border border-border hover:border-border-strong hover:bg-surface',
  ghost: 'text-text-muted hover:bg-accent-soft hover:text-text',
  danger: 'bg-danger text-white hover:brightness-105',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-4 text-[0.8125rem]',
  md: 'h-11 px-6 text-[0.9375rem]',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  trailingIcon,
  leadingIcon,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button type={type} className={cn(BASE, VARIANTS[variant], SIZES[size], className)} {...rest}>
      {leadingIcon}
      <span>{children}</span>
      {trailingIcon ? (
        <span
          aria-hidden="true"
          className={cn(
            'flex size-7 items-center justify-center rounded-full',
            'bg-black/8 transition-transform duration-300 ease-[var(--ease-gentle)]',
            'group-hover:translate-x-0.5 group-hover:scale-105 dark:bg-white/12',
          )}
        >
          {trailingIcon}
        </span>
      ) : null}
    </button>
  );
}
